import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import { CapacitorSilentNotifications } from 'capacitor-plugin-silent-notifications';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { App } from '@capacitor/app';
import Backend from '../../backend';
import store from '../../store';
import { toggleDialog } from '../../store/control/duck';
import { getBrowserDefaultLanguage } from '../locale';
import { processInternalRoute } from './utils';
import { setBadge } from './badge';
import { getGlobalIntl } from '../../reduxConnectedIntlProvider';

const platform = Capacitor.getPlatform();
const PUBLIC_URL = process.env.PUBLIC_URL;
var listener = null;

export const getPushNotificationToken = async () => {
  try {
    const storageResult = await Preferences.get({ key: 'pushToken' });
    return storageResult.value;
  } catch (error) {
    console.error('Error getting push notification token from storage:', error);
  }
};

const processPushNotificationAction = notificationAction => {
  const { notification } = notificationAction || {};
  let data = notification?.data || {};

  if (platform === 'ios') {
    data = notification.data.data;
  } else if (platform === 'android' && data.pinpoint) {
    if (typeof data.pinpoint === 'string') {
      data.pinpoint = JSON.parse(data.pinpoint);
    }
  }

  const { pinpoint } = data;
  const { deeplink } = pinpoint || {};
  if (deeplink) {
    const hostnames = [PUBLIC_URL, 'app.portfoplus.com', 'beta-app.portfoplus.com'];
    const foundHostname = hostnames.some(hostname => deeplink.includes(hostname));

    if (foundHostname) {
      processInternalRoute(deeplink);
    } else {
      window.open(deeplink, '_blank');
    }
  }
};

const addListener = () => {
  LocalNotifications.addListener('localNotificationActionPerformed', processPushNotificationAction);
  PushNotifications.addListener('pushNotificationActionPerformed', processPushNotificationAction);
  CapacitorSilentNotifications.addListener('silentNotificationReceived', notification => {
    const { data } = notification || {};
    const { jsonBody } = data || {};
    if (jsonBody?.isSilent && jsonBody.type === 'badge') {
      setBadge(jsonBody.badgeCount);
    }
  });
  PushNotifications.addListener('registration', async token => {
    if (token?.value) {
      Preferences.set({ key: 'pushToken', value: token.value });

      const appInfo = await App.getInfo();
      const locale = getBrowserDefaultLanguage();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      await fetch(Backend.methodUrl('endpoints/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token.value,
          appVersion: appInfo.version,
          locale: locale,
          timezone: timezone,
          nativePlatform: platform
        }),
        credentials: 'include'
      });
    }
  });
};

const registerPushNotification = async () => {
  try {
    await PushNotifications.register();
    addListener();
  } catch (error) {
    console.error('Error registering push notifications:', error);
  }
};

export const unregisterPushNotification = async () => {
  try {
    const token = await getPushNotificationToken();
    if (token) {
      await PushNotifications.unregister();
      await fetch(Backend.methodUrl('endpoints/unregister'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token }),
        credentials: 'include'
      });
      await Preferences.remove({ key: 'pushToken' });
    }
  } catch (error) {
    console.error('Error unregistering push notifications:', error);
  }
};

// Add app state listener for IOS
async function addAppStateListener(setPermissionGranted) {
  async function handleAppStateChange(state) {
    if (state.isActive) {
      if (listener) {
        listener.remove();
        listener = null;
      }

      const pushNotificationPermissionStatus = await PushNotifications.checkPermissions();
      const localNotificationPermissionStatus = await LocalNotifications.checkPermissions();
      if (
        pushNotificationPermissionStatus.receive === 'granted' &&
        localNotificationPermissionStatus.display === 'granted'
      ) {
        if (setPermissionGranted) {
          setPermissionGranted(true);
        }
        await registerPushNotification();
      }
    }
  }

  if (listener) {
    listener.remove();
    listener = null;
  }
  listener = await App.addListener('appStateChange', handleAppStateChange);
}

export async function requestPermissions(initialAttempt, setPermissionGranted = null) {
  let permissionGranted = false;
  let pushNotificationPermissionStatus = await PushNotifications.checkPermissions();
  let localNotificationPermissionStatus = await LocalNotifications.checkPermissions();
  let iosWillShowPrompt =
    pushNotificationPermissionStatus.receive === 'prompt' && localNotificationPermissionStatus.display === 'prompt';

  if (
    pushNotificationPermissionStatus.receive === 'granted' &&
    localNotificationPermissionStatus.display === 'granted'
  ) {
    permissionGranted = true;
  } else {
    pushNotificationPermissionStatus = await PushNotifications.requestPermissions();
    if (pushNotificationPermissionStatus.receive === 'granted') {
      localNotificationPermissionStatus = await LocalNotifications.requestPermissions();
    }

    if (
      pushNotificationPermissionStatus.receive === 'granted' &&
      localNotificationPermissionStatus.display === 'granted'
    ) {
      permissionGranted = true;
    } else if (!initialAttempt || !iosWillShowPrompt) {
      // Ask the user to go to settings
      await NativeSettings.open({
        optionAndroid: AndroidSettings.AppNotification,
        optionIOS: IOSSettings.App
      });

      if (platform === 'android') {
        pushNotificationPermissionStatus = await PushNotifications.checkPermissions();
        localNotificationPermissionStatus = await LocalNotifications.checkPermissions();
        if (
          pushNotificationPermissionStatus.receive === 'granted' &&
          localNotificationPermissionStatus.display === 'granted'
        ) {
          permissionGranted = true;
        }
      } else if (platform === 'ios') {
        addAppStateListener(setPermissionGranted);
      }
    }
  }

  if (permissionGranted) {
    if (setPermissionGranted) {
      setPermissionGranted(true);
    }
    await registerPushNotification();
    if (platform === 'android') {
      PushNotifications.addListener('pushNotificationReceived', async notification => {
        try {
          const pinpointData = notification.data.pinpoint || {};
          const title = notification.title || pinpointData.title;
          const body = notification.body || pinpointData.body;
          const data = notification.data || pinpointData.data;

          if (title || body) {
            await LocalNotifications.schedule({
              notifications: [
                {
                  smallIcon: 'ic_stat_logo',
                  iconColor: '#3966f8',
                  id: new Date().getTime(),
                  title: title,
                  body: body,
                  data: data,
                  actionTypeId: 'local-push-notification'
                }
              ]
            });
          }
        } catch (error) {
          console.error('Error handling push notification on Android:', error);
        }
      });
    }
  }
}

const checkNotificationPermission = async () => {
  const pushNotificationPermissionStatus = await PushNotifications.checkPermissions();
  const localNotificationPermissionStatus = await LocalNotifications.checkPermissions();
  return (
    pushNotificationPermissionStatus.receive === 'granted' && localNotificationPermissionStatus.display === 'granted'
  );
};

const getTimeStampCount = days => {
  const timeStampCount = days * 24 * 60 * 60 * 1000;
  // console.log("getTimeStampCount ::: timeStampCount ::: ", timeStampCount, " days ::: ", days)
  return timeStampCount;
};

const getPreviewImagePath = () => {
  const platform = Capacitor.getPlatform();
  const locale = getGlobalIntl().locale;
  return `img/permission_showcase/noti/${platform}/${locale}.png`;
};

export const checkAndRequestPermissions = async (
  signIn,
  permissionsKey = 'permissionsRequested',
  nextRequestTime = 7
) => {
  if (Capacitor.isNativePlatform()) {
    const currentDateTime = new Date().getTime();

    // console.log('checkAndRequestPermissions ::: Capacitor.isNativePlatform() ::: ');
    const { value: permissionsRequested } = await Preferences.get({ key: permissionsKey });
    const permissionGranted = await checkNotificationPermission();
    const diff = getTimeStampCount(nextRequestTime);
    // console.log("checkAndRequestPermissions ::: permissionGranted ::: ", permissionGranted)
    // console.log("checkAndRequestPermissions ::: currentDateTime ::: ", currentDateTime, " permissionsRequested ::: ", permissionsRequested,  " permissionsRequested time difference: ", (currentDateTime - permissionsRequested))
    if (
      !permissionGranted &&
      (!permissionsRequested || (permissionsRequested && currentDateTime - permissionsRequested > diff))
    ) {
      store.dispatch(
        toggleDialog('permission', {
          previewImage: getPreviewImagePath(),
          intlId: 'push-notification-required-msg-body',
          title: 'push-notification-required-msg-title',
          cta: 'push-notification-required-msg-cta',
          onClose: async () => {
            await requestPermissions(true);
            await Preferences.set({
              key: permissionsKey,
              value: currentDateTime.toString()
            });
          }
        })
      );
    } else {
      addListener();

      const sendTokenToBackend = async token => {
        fetch(Backend.methodUrl('endpoints/signIn'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token }),
          credentials: 'include'
        });
      };

      // get push notification token
      let token = await getPushNotificationToken();

      // if push notification token is not set, request permissions
      // due to the fact that the user has granted permission already, and the token is not set
      // THIS should be removed after certain time period

      const pushNotificationTokenRecoveryKey = 'pushNotificationTokenRecovery';
      const { value: pushNotificationTokenRecovery } = await Preferences.get({ key: pushNotificationTokenRecoveryKey });
      // console.log('checkAndRequestPermissions ::: pushNotificationTokenRecovery ::: ', pushNotificationTokenRecovery);
      if (!!permissionGranted && !pushNotificationTokenRecovery && !token) {
        // console.log("permission check /// and token is undefined /// request permissions")
        await requestPermissions(true);
        Preferences.set({
          key: pushNotificationTokenRecoveryKey,
          value: currentDateTime.toString()
        });
        token = await getPushNotificationToken();
        if (token && !signIn) {
          // console.log("since recovery is done, and signIn is false, should send token to backend for update: ", token)
          sendTokenToBackend(token);
        }
      } else if (!!permissionGranted && !pushNotificationTokenRecovery && token) {
        sendTokenToBackend(token);
        Preferences.set({
          key: pushNotificationTokenRecoveryKey,
          value: currentDateTime.toString()
        });
      }

      // Remove the recovery key after certain time period

      // console.log("checkAndRequestPermissions ::: is signIn action? ::: ", signIn)
      if (signIn) {
        token = await getPushNotificationToken();

        if (token) {
          sendTokenToBackend(token);
        }
      }
    }
  }
};

checkAndRequestPermissions(false);
